import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderUser from "../../components/HeaderUser";
import { Campaign, UserService } from "../../services/UserService";
import { NotificationService } from "../../services/NotificationService";
import { NotificationTemplate, NotificationChannelPreferences } from "../../types/Notification";
import { NotificationFrequency } from "../../types/enums";
import { formatNotificationCopy } from "../../utils/formatNotificationCopy";
import "./style.css";


const frequencyLabels: Record<NotificationFrequency, { label: string; hint?: string }> = {
  [NotificationFrequency.WEEKLY]: { label: "Semanal" },
  [NotificationFrequency.MONTHLY]: { label: "Mensal" },
  [NotificationFrequency.CRON]: { label: "Por meta" },
  [NotificationFrequency.DAILY]: { label: "Diário" },
  [NotificationFrequency.ONCE]: { label: "Único" },
};

const WhatsAppIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true">
    <path
      d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.5 9.5c.7-.5 1.7-.4 2.3.2.5.5.6 1.3.3 2-.2.5-.5.9-.7 1.3-.2.3-.3.6-.1.8.1.2.3.3.6.3.5 0 1.3-.3 1.7-.6.6-.5 1-1.2 1.1-2 .1-.8-.2-1.6-.9-2-1.1-.7-2.5-.2-3.1.9z"
      fill="currentColor"
      opacity="0.8"
    />
  </svg>
);

const EmailIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true">
    <rect
      x="3.5"
      y="6"
      width="17"
      height="12"
      rx="2.2"
      ry="2.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <polyline
      points="4 8.5 12 13.5 20 8.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const fallbackCampaign: Campaign = {
  id: 0,
  name: "Campanha Teste",
  dashboard_url: "about:blank",
  platform_id: "TESTE",
  matomo_id: "TESTE",
  primary_color: "#0b3d91",
  secondary_color: "#ffffff",
  chatbot_enabled: true,
};

const hexToRgb = (hex: string) => {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
        .split("")
        .map((char) => char + char)
        .join("")
      : normalized;
  const value = parseInt(expanded, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return { r, g, b };
};

const mixWithWhite = (hex: string, strength = 0.6) => {
  const { r, g, b } = hexToRgb(hex);
  const mix = (channel: number) =>
    Math.round(channel + (255 - channel) * Math.min(Math.max(strength, 0), 1));
  const toHex = (channel: number) => channel.toString(16).padStart(2, "0");
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
};

const Notifications: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [togglingTemplate, setTogglingTemplate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userPhone, setUserPhone] = useState<string | null>(null);
  const [channelError, setChannelError] = useState<"email" | "whatsapp" | null>(null);
  const [channelPrefs, setChannelPrefs] = useState<NotificationChannelPreferences | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await UserService.getUserWithCamaigns();
        setUserEmail(data.email ?? null);
        setUserPhone(data.phone ?? null);
        const availableCampaigns = data.campaigns && data.campaigns.length > 0 ? data.campaigns : [fallbackCampaign];
        setCampaigns(availableCampaigns);
        if (availableCampaigns.length > 0) {
          const saved = localStorage.getItem("selectedCampaignId");
          const current = saved ? availableCampaigns.find((c: Campaign) => c.id === Number(saved)) : availableCampaigns[0];
          setSelectedCampaign(current || availableCampaigns[0]);
        }
      } catch (err) {
        console.error("Erro ao carregar usuário/campanhas", err);
        setError("Não foi possível carregar suas campanhas.");
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      localStorage.setItem("selectedCampaignId", selectedCampaign.id.toString());
      loadTemplates(selectedCampaign.id);
      loadChannelPrefs(selectedCampaign.id);
    }
  }, [selectedCampaign]);

  const loadTemplates = async (campaignId?: number) => {
    if (!campaignId) {
      setTemplates([]);
      return;
    }
    try {
      setLoading(true);
      const response = await NotificationService.listTemplates(campaignId);
      setTemplates(response);
      setError(null);
    } catch (err: any) {
      console.error("Erro ao listar notificações", err);
      if (err?.response?.status === 404) {
        setTemplates([]);
        setError("Nenhuma notificação encontrada para esta campanha.");
      } else {
        setError("Erro ao carregar suas notificações.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateToggle = async (template: NotificationTemplate, nextActive: boolean) => {
    if (!selectedCampaign) return;
    setTogglingTemplate(template.template_id);
    setError(null);

    const previousTemplates = [...templates];
    setTemplates((prev) =>
      prev.map((t) =>
        t.template_id === template.template_id ? { ...t, active: nextActive } : t
      )
    );

    try {
      if (template.assignment_id) {
        await NotificationService.toggleStatus(template.assignment_id, nextActive, selectedCampaign.id);
      } else if (nextActive) {
        const created = await NotificationService.create({
          title: template.title,
          campaign_id: selectedCampaign.id,
          frequency: template.default_frequency,
          template_id: template.template_id,
          insight_period: template.recommended_period ?? undefined,
          message: template.message || "",
          active: true,
        });
        setTemplates((prev) =>
          prev.map((t) =>
            t.template_id === template.template_id
              ? { ...t, active: true, assignment_id: created.id }
              : t
          )
        );
      }
      await loadTemplates(selectedCampaign.id);
    } catch (err) {
      console.error("Erro ao alternar notificação", err);
      setTemplates(previousTemplates);
      setError("Não foi possível salvar sua preferência.");
    } finally {
      setTogglingTemplate(null);
    }
  };

  const onSelectCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
  };

  const availableTemplates = useMemo(() => templates.filter((t) => t.available), [templates]);
  const activeCount = useMemo(() => availableTemplates.filter((n) => n.active).length, [availableTemplates]);

  const toggleChannel = (channel: "email" | "whatsapp") => {
    if (!channelPrefs || !selectedCampaign) return;
    const isEnabling = !channelPrefs[channel];
    if (isEnabling) {
      if (channel === "email" && !userEmail) {
        setChannelError("email");
        return;
      }
      if (channel === "whatsapp" && !userPhone) {
        setChannelError("whatsapp");
        return;
      }
    }
    const nextPrefs = { ...channelPrefs, [channel]: !channelPrefs[channel], campaign_id: selectedCampaign.id };
    setChannelPrefs(nextPrefs);
    NotificationService.setChannelPreferences({
      campaign_id: selectedCampaign.id,
      email: nextPrefs.email,
      whatsapp: nextPrefs.whatsapp,
    }).catch((err) => {
      console.error("Erro ao salvar preferência de canal", err);
      // revert on error
      setChannelPrefs(channelPrefs);
      setError("Não foi possível salvar sua preferência de canal.");
    });
  };

  const loadChannelPrefs = async (campaignId: number) => {
    try {
      const prefs = await NotificationService.getChannelPreferences(campaignId);
      setChannelPrefs(prefs);
      setError(null);
    } catch (err) {
      console.error("Erro ao buscar preferências de canal", err);
      setChannelPrefs({
        campaign_id: campaignId,
        email: true,
        whatsapp: false,
      });
      setError("Não foi possível carregar suas preferências de canal. Usando padrão.");
    }
  };

  const circleCampaignColor = selectedCampaign?.primary_color ?? "#0b3d91";
  const mutedBorderColor = mixWithWhite(circleCampaignColor, 0.7);
  const { r, g, b } = hexToRgb(circleCampaignColor);
  const campaignShadow = `rgba(${r}, ${g}, ${b}, 0.15)`;
  const campaignStyle = {
    "--campaign-border-color": circleCampaignColor,
    "--campaign-border-muted": mutedBorderColor,
    "--campaign-shadow-color": campaignShadow,
  } as CSSProperties;
  const notificationCardStyle = {
    "--notification-border-muted": mixWithWhite(circleCampaignColor, 0.82),
    "--notification-border-active": circleCampaignColor,
    "--notification-shadow-color": `rgba(${r}, ${g}, ${b}, 0.08)`,
    "--notification-shadow-hover": `rgba(${r}, ${g}, ${b}, 0.18)`,
  } as CSSProperties;

  return (
    <div className="notifications-page user-feed" style={campaignStyle}>
      <HeaderUser
        campaigns={campaigns}
        selectedCampaign={selectedCampaign}
        onSelectCampaign={onSelectCampaign}
      />

      <div className="notifications-container">
        <div className="page-head">
          <div className="title-block">
            <p className="eyebrow">Notificações</p>
            <h2>Minhas notificações</h2>
            <p>Escolha quais insights estarão ativos para a campanha selecionada.</p>
          </div>
          <div className="status-chip">
            <span className="dot" />
            {activeCount} ativos
          </div>
        </div>

        {error && <div className="error-box">{error}</div>}

        {channelError && (
          <div className="channel-error">
            <div className="content">
              <h4>{channelError === "email" ? "E-mail não cadastrado" : "WhatsApp não cadastrado"}</h4>
              <p>
                Para ativar {channelError === "email" ? "e-mail" : "WhatsApp"} como canal, adicione seu{" "}
                {channelError === "email" ? "endereço de e-mail" : "telefone"} no perfil.
              </p>
              <div className="actions">
                <button className="secondary" onClick={() => setChannelError(null)}>
                  Voltar
                </button>
                <button onClick={() => navigate("/perfil")}>Adicionar no perfil</button>
              </div>
            </div>
          </div>
        )}

        <div className="channel-preferences">
          <div>
            <h4>Preferência de recebimento</h4>
            <p>Escolha por onde quer receber alertas desta campanha.</p>
          </div>
          <div className="channel-toggles">
            <label
              className={`channel-toggle email ${channelPrefs?.email ? "on" : ""}`}
              aria-pressed={!!channelPrefs?.email}
            >
              <input
                type="checkbox"
                checked={!!channelPrefs?.email}
                onChange={() => toggleChannel("email")}
                disabled={!channelPrefs}
              />
              <div className="channel-badge">
                <EmailIcon />
              </div>
              <span>Email</span>
              <span className="toggle-knob" aria-hidden />
            </label>
            <label
              className={`channel-toggle whatsapp ${channelPrefs?.whatsapp ? "on" : ""}`}
              aria-pressed={!!channelPrefs?.whatsapp}
            >
              <input
                type="checkbox"
                checked={!!channelPrefs?.whatsapp}
                onChange={() => toggleChannel("whatsapp")}
                disabled={!channelPrefs}
              />
              <div className="channel-badge">
                <WhatsAppIcon />
              </div>
              <span>WhatsApp</span>
              <span className="toggle-knob" aria-hidden />
            </label>
          </div>
        </div>

        <section className="insight-catalog">
          <div className="list-head">
            <div>
              <h3>Catálogo disponível</h3>
              <p>Selecione quais notificações quer receber.</p>
            </div>
            {loading && <span className="muted">Carregando...</span>}
          </div>
          <div className="insight-grid">
            {!loading && availableTemplates.length === 0 && (
              <div className="empty-state">
                <h4>Nenhuma notificação liberada</h4>
                <p>Quando um admin ativar itens no catálogo desta campanha, eles aparecerão aqui.</p>
              </div>
            )}
            {availableTemplates.map((template) => {
              const freq = frequencyLabels[template.default_frequency] ?? { label: "Personalizado" };
              const isActive = template.active ?? false;
              const isSaving = togglingTemplate === template.template_id;
              const hiddenPeriods = new Set(["last_7_days", "last_2_days"]);
              const showPeriod =
                template.recommended_period &&
                !hiddenPeriods.has(template.recommended_period);
              return (
                <div
                  key={template.template_id}
                  className={`insight-card ${isActive ? "active" : ""}`}
                  style={notificationCardStyle}
                >
                  <div className="card-meta">
                    <span className="pill strong">{freq.label}</span>
                    {showPeriod && <span className="pill muted">{template.recommended_period}</span>}
                  </div>
                  <div className="card-head">
                    <h4>{template.title}</h4>
                    <label className={`toggle ${isActive ? "on" : "off"}`}>
                      <input
                        type="checkbox"
                        checked={isActive}
                        disabled={isSaving || !selectedCampaign}
                        onChange={() => handleTemplateToggle(template, !isActive)}
                      />
                      <span className="slider" />
                    </label>
                  </div>
                  {template.description && <p>{formatNotificationCopy(template.description)}</p>}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Notifications;
